import { Input, Modal, ModalProps, Typography } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import { JobPostingDetail, CandidateDetail } from '../types';
import Form from './Form';

interface ActivateApplicantFormValues {
  annotation: string;
}

interface ActivateApplicantModalProps extends ModalProps {
  candidateId?: CandidateDetail['id'];
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (
    data: void,
    variables: ActivateApplicantFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const ActivateApplicantModal = ({
  jobPostingId,
  candidateId,
  onSuccess,
  okButtonProps,
  ...props
}: ActivateApplicantModalProps) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: candidateId,
          job_posting: jobPostingId,
        },
      },
    ],
    {
      select(data) {
        return data?.results?.[0];
      },
      enabled: !!candidateId && !!jobPostingId,
    }
  );

  const candidate = useAPIQuery(`/candidates/${candidateId}/`, {
    enabled: !!candidateId,
  });

  const mutation = useAPIMutation(
    async ({ annotation }: ActivateApplicantFormValues, api) => {
      const recruitmentProcesses = await api.get('/recruitment-processes/', {
        params: {
          candidate: candidateId,
          job_posting: jobPostingId,
        },
      });

      if (recruitmentProcesses.data?.results[0].status === 'INACTIVE') {
        await api.patch(`/recruitment-processes/${recruitmentProcesses.data?.results[0].id}/`, {
          status: 'ACTIVE',
          commentary: 'Justificativa: ' + annotation,
        });
      }
    },
    {
      onSuccess,
      onSettled: async (_, __) => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/recruitment-processes/');
        await queryClient.invalidateQueries(
          `/recruitment-processes/${recruitmentProcess.data?.id}/`
        );
        await queryClient.invalidateQueries('/candidate-history/');
      },
    }
  );

  return (
    <Modal
      onOk={() => form.submit()}
      okButtonProps={{ loading: mutation.isLoading, ...okButtonProps }}
      title={
        <Typography.Text>
          Retornar <Typography.Text strong>{candidate.data?.name}</Typography.Text>
        </Typography.Text>
      }
      {...props}
    >
      <Helmet>
        <title>Retornar {candidate.data?.name || '...'} | Recrutamento e Seleção</title>
      </Helmet>
      <Form<ActivateApplicantFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          annotation: '',
        }}
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      >
        <Typography.Paragraph>
          Ao confirmar, <Typography.Text strong>{candidate.data?.name}</Typography.Text> retornará
          ao processo seletivo.
        </Typography.Paragraph>
        <Form.Item
          label="Justificativa"
          name="annotation"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ActivateApplicantModal;
